import { preview, siteConfig } from '@/config'
import router, { base } from '@/router'

const { websiteId: website, enabled = false } = siteConfig.umami || {}

const {
  screen: { width, height },
  navigator: { language },
  location,
  document
} = window

const { hostname, origin } = location
const screen = `${width}x${height}`
const delayDuration = 600

let cache
let identity

const addSource = (url) => {
  const source = sessionStorage.getItem("source")
  if (source && url.indexOf("source") === -1) {
    const q = `source=${source}`
    if (url.indexOf("?") !== -1) {
      return `${url}&${q}`
    } else {
      return `${url}?${q}`
    }
  }
  return url
}

const transformUrl = (vueCurrRoute) => {
  let url = vueCurrRoute.fullPath
  const trans = ['channel', 'detail', 'store', 'order', 'order/list', 'order-vr', 'order-vr/list']

  if (trans.includes(vueCurrRoute.name)) {
    const { id, goods_id, store_id, type, ...attr } = vueCurrRoute.query
    if (id || goods_id || store_id || type) {
      url = `${vueCurrRoute.path}/${id || goods_id || store_id || type}?${new URLSearchParams(attr).toString()}`
    }
  }

  url = url.replace(/[&?](_t|preview|templateId|templateType|source)=\w+/g, '').slice(1)
  url = addSource(url)
  return `${origin}${base}${url}`
}

const getPayload = (current = false) => {
  const url = addSource(window.location.href.replace(/[&?]_t=\d+/, ''))
  const res = {
    website,
    screen,
    language,
    title: document.title,
    hostname,
    url: umami.to ? transformUrl(umami.to) : url,
    referrer: umami.from ? transformUrl(umami.from) : document.referrer.startsWith(origin) ? '' : document.referrer,
    id: identity ? identity : undefined
  }
  if (current) {
    res.url = router?.history?.current ? transformUrl(router.history.current) : url
    res.referrer = umami.from ? transformUrl(umami.from) : ''
  }
  return res
}

const send = async (payload, type = 'event') => {
  if (!payload) {
    return
  }
  if (!enabled) {
    return
  }

  try {
    const res = await fetch('/send', {
      method: 'POST',
      body: JSON.stringify({ type, payload }),
      headers: {
        'Content-Type': 'application/json',
        ...(typeof cache !== 'undefined' && { 'x-umami-cache': cache })
      },
      credentials: 'omit'
    })

    const data = await res.json()
    if (data) {
      cache = data.cache
    }
  } catch (e) {
    /* no-op */
  }
}

const trackDo = (name, data) => {
  if (!website) {
    return Promise.resolve()
  }
  
  let trackPromise
  if (typeof name === 'string') {
    trackPromise = send({ ...getPayload(true), name, data })
  } else if (typeof name === 'object') {
    trackPromise = send({ ...name })
  } else if (typeof name === 'function') {
    trackPromise = send(name(getPayload(true)))
  } else {
    trackPromise = send(getPayload())
  }
  
  // 添加1秒超时
  return Promise.race([
    trackPromise,
    new Promise((resolve) => setTimeout(resolve, 1000))
  ])
}

// 通过监听<title>变化来执行trackDo，若200ms内未变化则自动执行
let lastTitle = document.title
let pendingTrack = null
let pendingTimer = null

const track = (name, data) => {
  if (!website) {
    return
  }
  pendingTrack = { name, data }

  // 清除之前的定时器，避免多次触发
  if (pendingTimer) {
    clearTimeout(pendingTimer)
    pendingTimer = null
  }

  // 200ms后如果还未触发，则自动执行
  pendingTimer = setTimeout(() => {
    if (pendingTrack) {
      trackDo(pendingTrack.name, pendingTrack.data)
      pendingTrack = null
    }
    pendingTimer = null
  }, 200)

  // 如果已经有监听器就不再重复添加
  if (!track._observer) {
    // 监听<title>元素的变化
    const titleElement = document.querySelector('title')
    if (titleElement) {
      const observer = new MutationObserver(() => {
        if (pendingTrack) {
          trackDo(pendingTrack.name, pendingTrack.data)
          pendingTrack = null
        }
        if (pendingTimer) {
          clearTimeout(pendingTimer)
          pendingTimer = null
        }
        lastTitle = document.title
      })
      observer.observe(titleElement, {
        childList: true,
        subtree: true,
        characterData: true
      })
      track._observer = observer
    }
  }
}

const identify = (id, data) => {
  if (typeof id === 'string') {
    identity = id
  }

  cache = ''
  return send(
    {
      ...getPayload(),
      data: typeof id === 'object' ? id : data
    },
    'identify'
  )
}

const eventRegex = /data-umami-event-([\w-_]+)/
const eventNameAttribute = 'data-umami-event'

const handleClicks = () => {
  const trackElement = async (el) => {
    let eventName = el.getAttribute(eventNameAttribute)
    if (!eventName) {
      return
    }

    const eventData = {}

    el.getAttributeNames().forEach((name) => {
      const match = name.match(eventRegex)
      if (match && el.getAttribute(name)) {
        eventData[match[1]] = el.getAttribute(name)
      }
    })

    return trackDo(eventName, eventData)
  }
  const onClick = async (e) => {
    const el = e.target
    const parentElement = el.closest('a,button')
    if (!parentElement) {
      const eventElement = el.closest(`[${eventNameAttribute}]`)
      if (eventElement) {
        return trackElement(eventElement)
      }
      return trackElement(el)
    }

    const { href } = parentElement
    if (parentElement.tagName === 'A' && href) {
      return trackElement(parentElement)
    }
    return trackElement(parentElement)
  }
  document.addEventListener('click', onClick, false)
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2)
}

function handleUid() {
  const uuid = localStorage.getItem('/uuid')
  const uname = localStorage.getItem('/uuname')
  if (uuid) {
    if (uname) {
      // 如果有uname则更新
      return identify(uuid, { uname })
    }
    return identify(uuid)
  } else {
    const newUuid = generateId()
    localStorage.setItem('/uuid', newUuid)
    return identify(newUuid)
  }
}

const source = () => {
  // 判断当前链接上是否存在source参数
  const source = new URLSearchParams(location.search).get("source")
  if (source) {
    sessionStorage.setItem("source", source)
  }
}

const init = () => {
  if (!website) {
    return
  }
  if (preview.inIframe) {
    return
  }

  // 来源
  source()

  // 路由变化监听
  window._urlChange = (to, from) => {
    umami.to = to
    umami.from = from
    const exclude = ['store', 'detail', 'channel']
    if (exclude.includes(to.name)) {
      return
    }
    umami.track()
  }

  // click 事件监听
  handleClicks()

  // uuid绑定
  handleUid().then()

}

const umami = {
  track,
  trackDo,
  identify,
  send,
  getPayload,
  to: '',
  from: ''
}

init()

export default umami
